import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Dumbbell, Sparkles, Plus, Trash2, Loader2 } from "lucide-react";
import { allCategories } from "@/lib/workoutCategories";
import { CategoryPicker } from "@/components/CategoryPicker";
import { WorkoutLibrary } from "@/components/WorkoutLibrary";

const TargetToggle = ({ forLibrary, onChange, prefix }) => (
  <div>
    <Label>Criar para</Label>
    <div className="grid grid-cols-2 gap-2 mt-1">
      <Button type="button" variant={forLibrary ? "outline" : "default"} onClick={()=>onChange(false)} className={!forLibrary ? "bg-rose-600 hover:bg-rose-700 text-white" : ""} data-testid={`${prefix}-target-student`}>Um aluno</Button>
      <Button type="button" variant={forLibrary ? "default" : "outline"} onClick={()=>onChange(true)} className={forLibrary ? "bg-rose-600 hover:bg-rose-700 text-white" : ""} data-testid={`${prefix}-target-library`}>Biblioteca de Treinos</Button>
    </div>
  </div>
);

export default function Workouts() {
  const [workouts, setWorkouts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [view, setView] = useState("students");
  const [students, setStudents] = useState([]);
  const [openAI, setOpenAI] = useState(false);
  const [openManual, setOpenManual] = useState(false);
  const [aiForm, setAiForm] = useState({ student_id: "", for_library: false, category: "Outros", goal: "", experience_level: "iniciante", days_per_week: 3, focus_areas: "", notes: "" });
  const [manualForm, setManualForm] = useState({ student_id: "", for_library: false, category: "Outros", title: "", goal: "", days: [{ label: "A", focus: "", exercises: [{ name: "", sets: 3, reps: "12", rest_seconds: 60, load: "", notes: "" }] }] });
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    const [w, s, t] = await Promise.all([api.get("/workouts"), api.get("/students"), api.get("/workouts/templates")]);
    setWorkouts(w.data); setStudents(s.data); setTemplates(t.data);
  };
  useEffect(() => { load(); }, []);

  const generateAI = async () => {
    if (!aiForm.for_library && !aiForm.student_id) return toast.error("Selecione um aluno");
    setGenerating(true);
    try {
      await api.post("/workouts/ai-generate", { ...aiForm, student_id: aiForm.for_library ? null : aiForm.student_id });
      toast.success(aiForm.for_library ? "Treino salvo na Biblioteca de Treinos!" : "Treino gerado pela IA!");
      setOpenAI(false); if (aiForm.for_library) setView("library"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro na geração"); }
    finally { setGenerating(false); }
  };

  const saveManual = async () => {
    if ((!manualForm.for_library && !manualForm.student_id) || !manualForm.title) return toast.error(manualForm.for_library ? "Preencha o título" : "Preencha aluno e título");
    if (manualForm.for_library && !manualForm.category.trim()) return toast.error("Informe a categoria");
    try {
      await api.post("/workouts", { ...manualForm, student_id: manualForm.for_library ? null : manualForm.student_id });
      toast.success(manualForm.for_library ? "Treino salvo na Biblioteca de Treinos!" : "Treino salvo"); setOpenManual(false);
      if (manualForm.for_library) setView("library");
      load();
    } catch (e) { toast.error("Erro"); }
  };

  const addDay = () => setManualForm({ ...manualForm, days: [...manualForm.days, { label: String.fromCharCode(65 + manualForm.days.length), focus: "", exercises: [{ name: "", sets: 3, reps: "12", rest_seconds: 60, load: "", notes: "" }] }] });
  const addEx = (di) => {
    const days = [...manualForm.days];
    days[di].exercises.push({ name: "", sets: 3, reps: "12", rest_seconds: 60, load: "", notes: "" });
    setManualForm({ ...manualForm, days });
  };
  const updateEx = (di, ei, field, value) => {
    const days = [...manualForm.days];
    days[di].exercises[ei][field] = value;
    setManualForm({ ...manualForm, days });
  };

  const remove = async (id) => { if (!window.confirm("Remover?")) return; await api.delete(`/workouts/${id}`); toast.success("Removido"); load(); };
  const studentName = (id) => students.find(s => s.id === id)?.name || "?";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Treinos</h1>
          <p className="text-slate-500">Fichas personalizadas + geração por IA</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={openManual} onOpenChange={setOpenManual}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="workout-manual-button"><Plus className="w-4 h-4 mr-2"/>Manual</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Ficha manual</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-4">
                <TargetToggle prefix="workout-manual" forLibrary={manualForm.for_library} onChange={(v)=>setManualForm({...manualForm,for_library:v})}/>
                <div className="grid md:grid-cols-2 gap-3">
                  {manualForm.for_library ? (
                    <div>
                      <Label>Categoria</Label>
                      <CategoryPicker value={manualForm.category} onChange={(v)=>setManualForm({...manualForm,category:v})} categories={allCategories(templates)} testid="workout-manual-category"/>
                    </div>
                  ) : (
                  <div>
                    <Label>Aluno</Label>
                    <Select value={manualForm.student_id} onValueChange={(v)=>setManualForm({...manualForm,student_id:v})}>
                      <SelectTrigger data-testid="workout-student-select"><SelectValue placeholder="Selecionar"/></SelectTrigger>
                      <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  )}
                  <div><Label>Título</Label><Input value={manualForm.title} onChange={(e)=>setManualForm({...manualForm,title:e.target.value})} data-testid="workout-manual-title"/></div>
                </div>
                <div><Label>Objetivo</Label><Input value={manualForm.goal} onChange={(e)=>setManualForm({...manualForm,goal:e.target.value})}/></div>
                {manualForm.days.map((d, di) => (
                  <Card key={di} className="p-4 border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-heading font-bold">Ficha {d.label}</div>
                      <Input placeholder="Foco (ex: peito e tríceps)" value={d.focus} onChange={(e)=>{const days=[...manualForm.days]; days[di].focus=e.target.value; setManualForm({...manualForm,days});}} className="max-w-xs"/>
                    </div>
                    {d.exercises.map((ex, ei) => (
                      <div key={ei} className="grid grid-cols-12 gap-2 mb-2">
                        <Input className="col-span-5" placeholder="Exercício" value={ex.name} onChange={(e)=>updateEx(di,ei,"name",e.target.value)}/>
                        <Input className="col-span-2" placeholder="Séries" type="number" value={ex.sets} onChange={(e)=>updateEx(di,ei,"sets",parseInt(e.target.value))}/>
                        <Input className="col-span-2" placeholder="Reps" value={ex.reps} onChange={(e)=>updateEx(di,ei,"reps",e.target.value)}/>
                        <Input className="col-span-3" placeholder="Carga" value={ex.load} onChange={(e)=>updateEx(di,ei,"load",e.target.value)}/>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" onClick={()=>addEx(di)} data-testid="workout-add-exercise-button">+ Exercício</Button>
                  </Card>
                ))}
                <Button variant="outline" onClick={addDay} className="w-full">+ Adicionar ficha</Button>
                <Button onClick={saveManual} data-testid="workout-save-and-assign-button" className="w-full bg-rose-600 hover:bg-rose-700">Salvar treino</Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={openAI} onOpenChange={setOpenAI}>
            <DialogTrigger asChild>
              <Button data-testid="workout-ai-button" className="bg-slate-950 hover:bg-slate-800 text-white"><Sparkles className="w-4 h-4 mr-2"/>Gerar com IA</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-rose-600"/>Treino com IA (GPT-5.4)</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-4">
                <TargetToggle prefix="workout-ai" forLibrary={aiForm.for_library} onChange={(v)=>setAiForm({...aiForm,for_library:v})}/>
                {aiForm.for_library ? (
                  <div>
                    <p className="text-xs text-slate-500 mb-2" data-testid="workout-ai-library-hint">O treino fica salvo na Biblioteca de Treinos, sem aluno, e pode ser aplicado a qualquer aluno depois.</p>
                    <Label>Categoria</Label>
                    <CategoryPicker value={aiForm.category} onChange={(v)=>setAiForm({...aiForm,category:v})} categories={allCategories(templates)} testid="workout-ai-category"/>
                  </div>
                ) : (
                <div>
                  <Label>Aluno</Label>
                  <Select value={aiForm.student_id} onValueChange={(v)=>setAiForm({...aiForm,student_id:v})}>
                    <SelectTrigger data-testid="workout-ai-student-select"><SelectValue placeholder="Selecionar aluno"/></SelectTrigger>
                    <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                )}
                <div><Label>Objetivo</Label><Input value={aiForm.goal} onChange={(e)=>setAiForm({...aiForm,goal:e.target.value})} placeholder="Ex: hipertrofia peito e costas" data-testid="workout-ai-prompt-input"/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nível</Label>
                    <Select value={aiForm.experience_level} onValueChange={(v)=>setAiForm({...aiForm,experience_level:v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="iniciante">Iniciante</SelectItem>
                        <SelectItem value="intermediario">Intermediário</SelectItem>
                        <SelectItem value="avancado">Avançado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Dias/semana</Label><Input type="number" min={2} max={6} value={aiForm.days_per_week} onChange={(e)=>setAiForm({...aiForm,days_per_week:parseInt(e.target.value)})}/></div>
                </div>
                <div><Label>Focos e restrições</Label><Textarea rows={3} value={aiForm.notes} onChange={(e)=>setAiForm({...aiForm,notes:e.target.value})} placeholder="Ex: evitar impacto no joelho"/></div>
                <Button onClick={generateAI} disabled={generating} data-testid="workout-ai-generate-button" className="w-full bg-rose-600 hover:bg-rose-700">
                  {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Gerando...</> : <><Sparkles className="w-4 h-4 mr-2"/>Gerar treino</>}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {[["students", `Fichas dos alunos (${workouts.length})`], ["library", `Biblioteca de Treinos (${templates.length})`]].map(([k, l]) => (
          <button key={k} onClick={()=>setView(k)} data-testid={`workouts-view-${k}`}
            className={`px-3 py-2 text-sm font-heading font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${view === k ? "border-rose-600 text-rose-600" : "border-transparent text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {view === "library" ? <WorkoutLibrary templates={templates} onReload={load}/> : (
      <div className="grid md:grid-cols-2 gap-4" data-testid="workouts-list">
        {workouts.map(w => (
          <Card key={w.id} className="p-5 border border-slate-200">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">{studentName(w.student_id)}</div>
                <h3 className="font-heading text-lg font-bold text-slate-900">{w.title}</h3>
                <p className="text-sm text-slate-500">{w.goal}</p>
              </div>
              <div className="flex items-center gap-1">
                {w.generated_by_ai && <span className="text-xs bg-rose-50 text-rose-700 px-2 py-1 rounded-full font-semibold flex items-center gap-1"><Sparkles className="w-3 h-3"/>IA</span>}
                <Button variant="ghost" size="icon" onClick={()=>remove(w.id)}><Trash2 className="w-4 h-4 text-slate-400"/></Button>
              </div>
            </div>
            <div className="space-y-3 mt-4">
              {w.days.map((d, i) => (
                <div key={i} className="border-l-4 border-rose-600 pl-3">
                  <div className="text-sm font-bold text-slate-900">Ficha {d.label} - {d.focus}</div>
                  <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                    {d.exercises.map((ex, j) => <div key={j}>• {ex.name} — {ex.sets}x{ex.reps} {ex.load && `@ ${ex.load}`}</div>)}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
        {workouts.length === 0 && <p className="col-span-full text-center text-slate-500 py-12">Nenhum treino</p>}
      </div>
      )}
    </div>
  );
}
